import React, { useState, useCallback, useEffect, useMemo } from 'react';
import SearchHeader from './components/search/SearchHeader';
import SearchDiagnostics from './components/search/SearchDiagnostics';
import AppliedFilterChips from './components/search/AppliedFilterChips';
import FiltersRail from './components/filters/FiltersRail';
import ResultsTable from './components/results/ResultsTable';
import DetailPanel from './components/detail/DetailPanel';
import TariffRail from './components/detail/TariffRail';
import SimilarRail from './components/detail/SimilarRail';
import LoadingSpinner from './components/common/LoadingSpinner';
import EmptyState from './components/common/EmptyState';
import ErrorMessage from './components/common/ErrorMessage';
import ThemeToggle from './components/common/ThemeToggle';
import SemanticPriceTab from './components/analytics/SemanticPriceTab';
import DeclarationPracticeTab from './components/analytics/DeclarationPracticeTab';
import { Layers, Search as SearchIcon, TrendingUp } from 'lucide-react';
import { useSearch } from './hooks/useSearch';
import { useFilters } from './hooks/useFilters';
import { useTheme } from './hooks/useTheme';
import { prefetchTariffTree } from './hooks/useTariffTree';
import { filterSignature, getActiveFilterChips } from './utils/filterDisplay';
import { isModeNotApplicable } from './utils/searchDisplay';

function TabNav({ active, onChange, themeProps }) {
  const tabs = [
    { id: 'search',    label: 'Пошук',           Icon: SearchIcon },
    { id: 'analytics', label: 'Аналітика цін',   Icon: TrendingUp },
    { id: 'practice',  label: 'Практика оформлення', Icon: Layers },
  ];
  return (
    <div className="flex items-center border-b border-border-default bg-surface">
      {tabs.map(t => {
        const Act = t.Icon;
        const isActive = active === t.id;
        return (
          <button key={t.id} type="button" onClick={() => onChange(t.id)}
            className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold border-b-2 transition-colors
              ${isActive
                ? 'border-blue-600 text-blue-700 bg-blue-50 dark:border-blue-400 dark:text-blue-300 dark:bg-blue-950/40'
                : 'border-transparent text-text-secondary hover:text-text-primary hover:bg-hover'}`}>
            <Act className="w-4 h-4" />
            {t.label}
          </button>
        );
      })}
      <div className="ml-auto pr-3">
        <ThemeToggle {...themeProps} />
      </div>
    </div>
  );
}

export default function App() {
  const { theme, source, setLight, setDark, setAuto } = useTheme();
  const themeProps = { theme, source, onLight: setLight, onAuto: setAuto, onDark: setDark };

  const [tab, setTab] = useState('search');
  // якорь для семантичной ценовой аналитики (передаётся из search → /semantic_price)
  const [semanticAnchor, setSemanticAnchor] = useState({ anchorId: null, query: '', codePrefix: '' });
  const [practiceAnchor, setPracticeAnchor] = useState({ anchorId: null, query: '', codePrefix: '' });
  const {
    results,
    loading,
    error,
    searchInfo,
    totalEstimate,
    suggestion,
    phrasingAlternatives,
    currentPage,
    pageSize,
    performSearch,
    goToPage,
  } = useSearch();

  const {
    filters,
    countries,
    years,
    updateFilter,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  } = useFilters();

  const [selectedDeclaration, setSelectedDeclaration] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [tariffOpen, setTariffOpen] = useState(false);
  const [tariffFocusCode, setTariffFocusCode] = useState(null);
  const [similarOpen, setSimilarOpen] = useState(false);
  const [declarationHistory, setDeclarationHistory] = useState([]);
  const [lastQuery, setLastQuery] = useState('');
  const [lastMode, setLastMode] = useState('auto');
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const activeFilterChips = useMemo(() => getActiveFilterChips(filters), [filters]);
  const visibleActiveFilterCount = activeFilterChips.length || activeFilterCount;
  const currentFilterSignature = useMemo(() => filterSignature(filters), [filters]);
  const appliedFilterSignature = useMemo(() => filterSignature(appliedFilters), [appliedFilters]);
  const hasPendingFilterChanges = Boolean(lastQuery) && currentFilterSignature !== appliedFilterSignature;

  useEffect(() => {
    let cancelled = false;
    let idleId = null;
    let timerId = null;

    const warmTariffTree = () => {
      if (cancelled) return;
      prefetchTariffTree().catch(() => {
        // The panel still shows its normal error state if the user opens it later.
      });
    };

    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(warmTariffTree, { timeout: 2500 });
    } else if (typeof window !== 'undefined') {
      timerId = window.setTimeout(warmTariffTree, 1500);
    }

    return () => {
      cancelled = true;
      if (idleId != null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      if (timerId != null) {
        window.clearTimeout(timerId);
      }
    };
  }, []);

  const handleSearch = useCallback((query, mode) => {
    setLastQuery(query);
    setLastMode(mode);
    setAppliedFilters(filters);
    setSelectedDeclaration(null); // Reset selection on new search
    setDeclarationHistory([]);
    performSearch(query, mode, filters, 0);
  }, [performSearch, filters]);

  const handlePageChange = useCallback((page) => {
    goToPage(page, appliedFilters);
  }, [goToPage, appliedFilters]);

  const handleSwitchSuggestedMode = useCallback(() => {
    const suggestedMode = searchInfo?.suggested_mode;
    if (lastQuery && suggestedMode) {
      handleSearch(lastQuery, suggestedMode);
    }
  }, [handleSearch, lastQuery, searchInfo]);

  const handleOpenCodePractice = useCallback(() => {
    const codePrefix = String(lastQuery || '').replace(/\D/g, '').slice(0, 10);
    setPracticeAnchor({
      anchorId: null,
      query: '',
      codePrefix,
    });
    setTab('practice');
  }, [lastQuery]);

  const handleRowClick = useCallback((row) => {
    setSelectedDeclaration(row);
    setTariffFocusCode(row?.product_code || null);
    setDeclarationHistory([]);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedDeclaration(null);
    setTariffOpen(false);
    setSimilarOpen(false);
    setDeclarationHistory([]);
  }, []);

  const handleToggleTariff = useCallback(() => {
    setTariffOpen(o => !o);
  }, []);

  const handleToggleSimilar = useCallback(() => {
    setSimilarOpen(o => !o);
  }, []);

  const handleFocusInTariff = useCallback((code) => {
    setTariffFocusCode(code);
    setTariffOpen(true);
  }, []);

  const handleOpenAsMain = useCallback((item) => {
    if (!item) return;
    setSelectedDeclaration(prev => {
      if (prev) setDeclarationHistory(h => [...h, prev]);
      return item;
    });
    setTariffFocusCode(item.product_code || null);
  }, []);

  const handleBackToPrevious = useCallback(() => {
    setDeclarationHistory(h => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setSelectedDeclaration(prev);
      setTariffFocusCode(prev?.product_code || null);
      return h.slice(0, -1);
    });
  }, []);

  const handleFilterUpdate = useCallback((key, value) => {
    updateFilter(key, value);
  }, [updateFilter]);

  const handleApplyFilters = useCallback(() => {
    if (lastQuery) {
      setAppliedFilters(filters);
      performSearch(lastQuery, lastMode, filters, 0);
    }
  }, [performSearch, lastQuery, lastMode, filters]);

  const hasResults = results.length > 0;
  const showInitial = !loading && !error && !hasResults && !lastQuery;
  const showNoResults = !loading && !error && !hasResults && Boolean(lastQuery);
  const modeNotApplicable = showNoResults && isModeNotApplicable(searchInfo);

  if (tab === 'analytics') {
    const initialQuery = semanticAnchor.anchorId ? '' : (semanticAnchor.query || lastQuery);
    return (
      <div className="min-h-screen bg-app">
        <TabNav active={tab} onChange={setTab} themeProps={themeProps} />
        <SemanticPriceTab
          initialAnchorId={semanticAnchor.anchorId}
          initialQuery={initialQuery}
          initialCodePrefix={semanticAnchor.codePrefix}
        />
      </div>
    );
  }

  if (tab === 'practice') {
    const initialQuery = practiceAnchor.anchorId ? '' : (practiceAnchor.query || lastQuery);
    return (
      <div className="min-h-screen bg-app">
        <TabNav active={tab} onChange={setTab} themeProps={themeProps} />
        <DeclarationPracticeTab
          initialAnchorId={practiceAnchor.anchorId}
          initialQuery={initialQuery}
          initialCodePrefix={practiceAnchor.codePrefix}
        />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-app">
      <TabNav active={tab} onChange={setTab} themeProps={themeProps} />

      <SearchHeader
        value={lastQuery}
        modeValue={lastMode}
        loading={loading}
        onSearch={handleSearch}
        activeFilterCount={visibleActiveFilterCount}
        hasPendingFilterChanges={hasPendingFilterChanges}
        onOpenFilters={() => setFiltersOpen(o => !o)}
        filtersOpen={filtersOpen}
        suggestion={suggestion}
        phrasingAlternatives={phrasingAlternatives}
      />

      <SearchDiagnostics
        searchInfo={searchInfo}
        totalEstimate={totalEstimate}
        loading={loading}
      />

      {!filtersOpen && (
        <AppliedFilterChips
          chips={activeFilterChips}
          onRemove={handleFilterUpdate}
          onApply={handleApplyFilters}
          hasPendingFilterChanges={hasPendingFilterChanges}
        />
      )}

      <div className="flex-1 min-h-0">
        <div className="flex h-full">
          <FiltersRail
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            filters={filters}
            countries={countries}
            years={years}
            onUpdateFilter={handleFilterUpdate}
            onClearFilters={clearFilters}
            onApply={handleApplyFilters}
            hasActiveFilters={hasActiveFilters}
            hasPendingFilterChanges={hasPendingFilterChanges}
            hasSearch={Boolean(lastQuery)}
          />

          {/* Column: Results */}
          <div className="flex-1 min-w-[360px] flex flex-col py-2 px-3">
            {loading && (
              <div className="flex-1 overflow-y-auto">
                <LoadingSpinner text="Поиск по 10.27M деклараций..." />
              </div>
            )}

            {error && (
              <div className="flex-1 overflow-y-auto">
                <ErrorMessage
                  message={error}
                  onRetry={() => lastQuery && handleSearch(lastQuery, lastMode)}
                />
              </div>
            )}

            {showInitial && (
              <div className="flex-1 overflow-y-auto">
                <EmptyState
                  message="Введите запрос для поиска"
                  hint="Код товара, артикул, описание на украинском, или торговая марка"
                />
              </div>
            )}

            {showNoResults && modeNotApplicable && (
              <div className="flex-1 overflow-y-auto">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200">
                  <div className="font-semibold">Этот режим не подходит для такого запроса</div>
                  <div className="mt-1 text-amber-800 dark:text-amber-200/80">
                    Похожее написание полезно для бренда, артикула или модели. Для описания товара используйте Авто или Похожие товары.
                  </div>
                  {searchInfo?.next_action === 'switch_mode' && searchInfo?.suggested_mode && (
                    <button
                      type="button"
                      onClick={handleSwitchSuggestedMode}
                      className="mt-3 inline-flex items-center rounded-md bg-amber-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-800 dark:bg-amber-500 dark:text-amber-950 dark:hover:bg-amber-400"
                    >
                      Перейти в Авто
                    </button>
                  )}
                </div>
              </div>
            )}

            {showNoResults && !modeNotApplicable && (
              <div className="flex-1 overflow-y-auto">
                <EmptyState
                  message="Ничего не найдено"
                  hint="Попробуйте уточнить запрос или изменить режим поиска."
                />
              </div>
            )}

            {!loading && !error && hasResults && (
              <ResultsTable
                results={results}
                searchInfo={searchInfo}
                totalEstimate={totalEstimate}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onRowClick={handleRowClick}
                onOpenPractice={searchInfo?.next_action === 'open_practice_analytics' ? handleOpenCodePractice : null}
                searchQuery={lastQuery}
                selectedId={selectedDeclaration?.original_id}
              />
            )}
          </div>

          {/* Column: Detail panel (conditional) */}
          {selectedDeclaration && (
            <DetailPanel
              declaration={selectedDeclaration}
              searchQuery={lastQuery}
              searchMethod={searchInfo?.methods_used?.join(', ')}
              onClose={handleCloseDetail}
              onAnalyzeSemantic={({ anchorId, codePrefix, fullCode }) => {
                setSemanticAnchor({
                  anchorId,
                  query: lastQuery || '',
                  codePrefix: fullCode || codePrefix || '',
                });
                setTab('analytics');
              }}
              onAnalyzePractice={({ anchorId, codePrefix, fullCode }) => {
                setPracticeAnchor({
                  anchorId,
                  query: lastQuery || '',
                  codePrefix: fullCode || codePrefix || '',
                });
                setTab('practice');
              }}
              tariffOpen={tariffOpen}
              onToggleTariff={handleToggleTariff}
              focusCode={tariffFocusCode}
              onFocusInTariff={handleFocusInTariff}
              similarOpen={similarOpen}
              onToggleSimilar={handleToggleSimilar}
              previousDeclaration={declarationHistory[declarationHistory.length - 1] || null}
              onBackToPrevious={declarationHistory.length > 0 ? handleBackToPrevious : null}
            />
          )}

          {/* Column: Tariff rail (conditional, opens on demand) */}
          {selectedDeclaration && tariffOpen && (
            <TariffRail
              open={tariffOpen}
              code={tariffFocusCode || selectedDeclaration.product_code}
              originalCode={selectedDeclaration.product_code}
              onClose={() => setTariffOpen(false)}
            />
          )}

          {/* Column: Similar rail (conditional, opens on demand) */}
          {selectedDeclaration && similarOpen && (
            <SimilarRail
              open={similarOpen}
              originalId={selectedDeclaration.original_id}
              originalCode={selectedDeclaration.product_code}
              searchQuery={lastQuery}
              onClose={() => setSimilarOpen(false)}
              onOpenAsMain={handleOpenAsMain}
            />
          )}
        </div>
      </div>
    </div>
  );
}
